import { useState } from "react";
import { EMPRESA, ESTADOS } from "../lib/config";
import { emptyAlbaran } from "../lib/storage";
import { SignaturePad } from "./SignaturePad";
import { AlbaranDocument } from "./AlbaranDocument";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Package, Truck, Save, Loader2, Building2, Eye, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, value, onChange, placeholder, type = "text", testid, required }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold tracking-wide uppercase text-slate-500">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className="h-11 text-base px-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
    />
  </div>
);

const PartyFields = ({ who, data, set, accent, icon: Icon, title, addrLabel }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
    <div className={`px-4 py-3 text-white font-bold flex items-center gap-2 ${accent}`}>
      <Icon className="h-5 w-5" /> {title}
    </div>
    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Nombre" value={data.nombre} onChange={(e) => set("nombre", e.target.value)} placeholder="Juan" testid={`${who}-nombre`} required />
      <Field label="Apellidos" value={data.apellidos} onChange={(e) => set("apellidos", e.target.value)} placeholder="García López" testid={`${who}-apellidos`} />
      <Field label="DNI" value={data.dni} onChange={(e) => set("dni", e.target.value)} placeholder="12345678A" testid={`${who}-dni`} />
      <Field label="Móvil" value={data.movil} onChange={(e) => set("movil", e.target.value)} placeholder="600 000 000" type="tel" testid={`${who}-movil`} />
      <div className="sm:col-span-2">
        <Field label={addrLabel} value={data.direccion} onChange={(e) => set("direccion", e.target.value)} placeholder="C/ Mayor, 1, Ciudad" testid={`${who}-direccion`} required />
      </div>
    </div>
  </div>
);

export const AlbaranForm = ({ initial, mode = "create", submitting, onSubmit }) => {
  const [form, setForm] = useState(() => ({ ...emptyAlbaran(), ...(initial || {}) }));
  const [preview, setPreview] = useState(false);

  const setParty = (party, key, value) =>
    setForm((f) => ({ ...f, [party]: { ...f[party], [key]: value } }));

  const validate = () => {
    if (!form.remitente.nombre.trim()) return "El nombre del remitente es obligatorio.";
    if (!form.remitente.direccion.trim()) return "La dirección de recogida es obligatoria.";
    if (!form.destinatario.nombre.trim()) return "El nombre del destinatario es obligatorio.";
    if (!form.destinatario.direccion.trim()) return "La dirección de entrega es obligatoria.";
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    onSubmit(form);
  };

  const openPreview = () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setPreview(true);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Estado */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5">
        <label className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-2 flex items-center gap-1.5">
          <ClipboardList className="h-4 w-4" /> Estado del albarán
        </label>
        <div className="flex flex-wrap gap-2" data-testid="estado-select">
          {ESTADOS.map((es) => {
            const active = form.estado === es.key;
            return (
              <button
                key={es.key}
                type="button"
                data-testid={`estado-${es.key}`}
                onClick={() => setForm((f) => ({ ...f, estado: es.key }))}
                className={`h-10 px-4 rounded-full text-sm font-semibold border-2 transition-colors ${
                  active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {es.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PartyFields who="remitente" data={form.remitente} set={(k, v) => setParty("remitente", k, v)} accent="bg-blue-600" icon={Package} title="Recogida · Remitente" addrLabel="Dirección de recogida" />
        <PartyFields who="destinatario" data={form.destinatario} set={(k, v) => setParty("destinatario", k, v)} accent="bg-violet-600" icon={Truck} title="Entrega · Destinatario" addrLabel="Dirección de entrega" />
      </div>

      {/* Observaciones */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5">
        <label className="text-xs font-semibold tracking-wide uppercase text-slate-500">Observaciones / Notas</label>
        <textarea
          value={form.observaciones}
          onChange={(e) => setForm((f) => ({ ...f, observaciones: e.target.value }))}
          placeholder="Indicaciones de la mercancía, incidencias, horario de entrega..."
          data-testid="input-observaciones"
          rows={3}
          className="mt-1.5 w-full text-base px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition resize-y"
        />
      </div>

      {/* Firmas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5">
          <SignaturePad label="Firma de Recogida" testid="firma-recogida" initialDataURL={form.firmaRecogida || ""} onChange={(v) => setForm((f) => ({ ...f, firmaRecogida: v }))} />
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5">
          <SignaturePad label="Firma de Entrega" testid="firma-entrega" initialDataURL={form.firmaEntrega || ""} onChange={(v) => setForm((f) => ({ ...f, firmaEntrega: v }))} />
        </div>
      </div>

      {/* Emisora */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 md:p-5">
        <h3 className="text-xs font-semibold tracking-wide uppercase text-slate-500 mb-2 flex items-center gap-1.5">
          <Building2 className="h-4 w-4" /> Datos de la empresa emisora
        </h3>
        <p className="font-bold text-slate-900">{EMPRESA.nombre} · {EMPRESA.persona}</p>
        <p className="text-sm text-slate-600">DNI/NIF: {EMPRESA.dni} · {EMPRESA.direccion}</p>
      </div>

      {/* Acciones */}
      <div className="flex flex-col sm:flex-row justify-end gap-3">
        <button
          type="button"
          onClick={openPreview}
          data-testid="preview-btn"
          className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg text-sm font-bold text-slate-700 border border-slate-300 hover:bg-slate-50 transition-colors"
        >
          <Eye className="h-5 w-5" /> Vista previa del albarán
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="save-albaran-btn"
          className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          {submitting ? "Guardando..." : mode === "edit" ? "Guardar cambios" : "Guardar albarán"}
        </button>
      </div>

      {/* Vista previa */}
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle>Vista previa del albarán</DialogTitle>
            <DialogDescription>Así quedará el albarán antes de guardarlo.</DialogDescription>
          </DialogHeader>
          <AlbaranDocument albaran={form} testid="preview-document" />
        </DialogContent>
      </Dialog>
    </form>
  );
};
